<template>
  <div
    class="promo-item"
    :class="{ 'promo-item--active': activeClass }"
    :style="gradient"
    data-name="promo_card"
    @click.stop="goToLink"
  >
    <div class="promo-item__img">
      <u-i-compressed-image :compressed-images="images" :alt="getLocale(promo.props.title)" />
    </div>

    <div class="promo-item__body">
      <div class="promo-item__description">
        <div class="promo-item__title" v-html="getLocale(promo.props.title)"></div>
        <div class="promo-item__text" v-html="getLocale(promo.props.description)"></div>
      </div>

      <ol-button
        v-if="!countdown"
        class="promo-item__button"
        color="gray-night-100"
        text-color="gray-night-900"
        :label="getLocale(promo.props.button)"
        data-name="promo_card-btn"
      />

      <button
        v-else
        class="promo-item__button"
        :class="[
          'promo-item__button--with-timer',
          'ol-btn',
          'text-gray-night-900',
          { 'promo-item__button--with-timer--hover': buttonHover },
          { 'promo-item__button--with-timer--not-hover': buttonNotHover },
        ]"
        type="button"
        data-name="promo_card-btn"
        @mouseover="buttonHover = true"
        @mouseout="buttonHover = false"
      >
        <span class="ol-btn__inner">
          <span class="promo-item__button-text">
            {{ getLocale(promo.props.button) }}
          </span>
          <span class="promo-item__button-timer">
            <img src="../../assets/icon/svg/timer-active.svg" alt="timer-active" />
            {{ countdown }}
          </span>
        </span>
      </button>
    </div>

    <div v-if="promoState === 'soon'" class="promo-item__badge promo-item__badge--soon">{{ $t('Soon') }}</div>
    <div v-else-if="promoState === 'expires'" class="promo-item__badge promo-item__badge--expires">
      {{ $t('Expires') }}
    </div>
    <div v-else-if="promoState === 'new'" class="promo-item__badge">new</div>
  </div>
</template>

<script setup>
import { ref, computed, onBeforeUnmount, watch, onMounted } from 'vue'
import UICompressedImage from 'components/ui/UICompressedImage.vue'
import { OlButton } from '@gamesite/olimp-core'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

const props = defineProps({
  promo: {
    type: Object,
    required: true,
  },
})

const emit = defineEmits(['expiredCard'])

const { locale, t } = useI18n()
const router = useRouter()

const lang = computed(() => locale.value)

const activeClass = ref(false)

const buttonHover = ref(false)
const buttonNotHover = ref(false)

watch(buttonHover, (val, oldVal) => {
  if (oldVal && !val) {
    buttonNotHover.value = true
    return
  }
  buttonNotHover.value = false
})

const image = computed(() => {
  const id = getLocale(props.promo.props.image)
  return props.promo.images.find((i) => i.id == id)
})

const images = computed(() => ({ xl: image.value?.compressedImage?.webp.xl }))

const gradient = computed(() => {
  const colorFrom = props.promo.props.gradientStart
  if (!colorFrom) {
    return ''
  }
  return `background: radial-gradient(100% 100% at 50% 0%, ${colorFrom} 0%, var(--promo-end-gradient) 100%);`
})

const promoState = ref('')
const countdown = ref('')

const HOUR_MS = 3600000
const DAY_MS = 86400000
const WEEK_MS = 168 * HOUR_MS

let countDownTimer = null

function updateCountdown() {
  const now = new Date()
  const dateStart = new Date(props.promo.props.contentDateStart || props.promo.dateStart)
  const dateEnd = new Date(props.promo.props.contentDateEnd || props.promo.dateEnd)

  if (!dateStart || !dateEnd) {
    countdown.value = ''
    clearTimeout(countDownTimer)
    return
  }

  const endDiffMs = dateStart > now ? dateStart - now : dateEnd - now

  if (endDiffMs <= 0) {
    countdown.value = ''
    clearTimeout(countDownTimer)
    return
  }

  const hours = Math.floor(endDiffMs / 3600000)

  if (hours < 24) {
    const totalSeconds = Math.floor(endDiffMs / 1000)
    const totalMinutes = Math.floor(totalSeconds / 60)
    const minutes = totalMinutes % 60
    const seconds = totalSeconds % 60

    countdown.value =
      hours > 0
        ? `${hours}${t('timer.oneLetter.hour')} ${minutes}${t('timer.oneLetter.minute')}`
        : `${minutes}${t('timer.oneLetter.minute')} ${seconds}${t('timer.oneLetter.second')}`
  }

  const nextUpdateMs = hours > 0 ? 60000 : 1000
  countDownTimer = setTimeout(() => requestAnimationFrame(updateCountdown), nextUpdateMs)
}

const promoId = computed(() => {
  return props.promo.id
})

function goToLink() {
  activeClass.value = !activeClass.value
  router.push({ name: 'Promotion', params: { id: promoId.value } }).catch(() => {})
}

function getLocale(locale) {
  if (!locale) {
    return ''
  }
  let result = locale[lang.value] ? locale[lang.value] : locale.ru

  if (typeof result === 'string') {
    const variables = result.match(/(-?\d+(\.\d+)?)/g)?.map((v) => +v)

    if (variables) {
      variables.forEach((num) => {
        if (num.toString().length > 4) {
          const newVariable = num
            .toLocaleString('ru-RU')
            .replace(/,/g, '.')
            .replaceAll(/\u00A0/g, '&nbsp')

          result = result.replaceAll(num.toString(), newVariable)
        }
      })
    }
  }
  return result
}

let expirationTimeout = null
function setupExpirationTimer() {
  const now = new Date()
  const dateEnd = new Date(props.promo.dateEnd)

  if (expirationTimeout) {
    clearTimeout(expirationTimeout)
  }

  const timeUntilExpiration = dateEnd - now
  if (timeUntilExpiration <= 0 || !timeUntilExpiration) {
    return
  }

  const DELAY_MS = 1000
  expirationTimeout = setTimeout(() => {
    emit('expiredCard')
  }, timeUntilExpiration + DELAY_MS)
}

let soonTimeout = null
let newTimeout = null
let expiresTimeout = null
function setupPromoStateTimers() {
  const now = new Date()
  const dateStart = new Date(props.promo.props.contentDateStart || props.promo.dateStart)
  const dateEnd = new Date(props.promo.props.contentDateEnd || props.promo.dateEnd)

  if (soonTimeout) {
    clearTimeout(soonTimeout)
  }
  if (newTimeout) {
    clearTimeout(newTimeout)
  }
  if (expiresTimeout) {
    clearTimeout(expiresTimeout)
  }

  if (props.promo.props.contentDateStart && now < dateStart) {
    promoState.value = 'soon'
    const timeUntilStart = dateStart - now
    soonTimeout = setTimeout(() => {
      promoState.value = ''
      setupPromoStateTimers()
    }, timeUntilStart)
    return
  }

  const startDiffMs = now - dateStart
  if (startDiffMs > 0 && startDiffMs <= WEEK_MS) {
    promoState.value = 'new'
    const timeUntilNewEnds = WEEK_MS - startDiffMs
    newTimeout = setTimeout(() => {
      promoState.value = ''
      setupPromoStateTimers()
    }, timeUntilNewEnds)
  }

  const endDiffMs = dateEnd - now
  if (endDiffMs > 0 && endDiffMs <= DAY_MS) {
    promoState.value = 'expires'
    expiresTimeout = setTimeout(() => {
      promoState.value = ''
    }, endDiffMs)
  }
}

onMounted(() => {
  setupExpirationTimer()
  setupPromoStateTimers()
  updateCountdown()
})

onBeforeUnmount(() => {
  if (expirationTimeout) {
    clearTimeout(expirationTimeout)
  }
  if (soonTimeout) {
    clearTimeout(soonTimeout)
  }
  if (newTimeout) {
    clearTimeout(newTimeout)
  }
  if (expiresTimeout) {
    clearTimeout(expiresTimeout)
  }
  if (countDownTimer) {
    clearTimeout(countDownTimer)
  }
})
</script>

<style lang="scss">
.promo-item {
  position: relative;
  display: flex;
  gap: 8px;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  padding: 0 16px 16px;
  border-radius: 12px;
  background: radial-gradient(100% 100% at 50% 0%, #0f2f90 0%, var(--promo-end-gradient) 100%);
  margin-top: 16px;
  width: calc((100% - 8px) / 2);

  @media (min-width: 580px) {
    width: calc((100% - 24px) / 3);
  }

  @media (min-width: 1024px) {
    width: calc((100% - 32px) / 3);
  }

  @media (min-width: 1440px) {
    width: calc((100% - 72px) / 4);
  }

  @media (hover: hover) {
    &:not(.promo-item--loader):hover {
      outline: 4px solid var(--cas-light-200);
    }
  }

  &--active {
    outline: 4px solid var(--cas-light-200);
  }

  &__img {
    max-width: 184px;
    width: 100%;
    margin-top: -16px;
    z-index: 3;

    img {
      display: flex;
      aspect-ratio: 23 / 21;
    }
  }

  &__body {
    width: 100%;
    min-height: 136px;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  &__description {
    margin-bottom: 16px;
  }

  &__title {
    color: var(--cas-light-900);
    font-size: 18px;
    line-height: 24px;
    font-weight: 500;
    text-align: center;
    margin-bottom: 8px;
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
  }

  &__text {
    color: var(--cas-light-500);
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
    text-align: center;
    overflow: hidden;
    white-space: pre-wrap;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;

    p,
    span,
    a {
      font-size: inherit;
      line-height: inherit;
      font-weight: inherit;
    }
  }

  .ol-btn {
    transition: all 0s;

    &::before {
      position: static;
    }

    &__inner {
      display: inline-block;
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      height: auto;
    }
  }

  &__button {
    width: 100%;
    overflow: hidden;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;

    &-text {
      padding: 0 8px;
      margin: 0;
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &--with-timer {
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: var(--cas-light-100);
      animation: 2s opacityChange ease-in-out infinite;

      .ol-btn__inner {
        width: 100%;
        display: flex;
        flex-direction: column;
        padding: 18px 0;
        gap: 20px;
        position: absolute;
        transform: translateY(-22%);
      }

      .promo-item__button-timer {
        display: flex;
        gap: 4px;
      }

      &--hover {
        animation: none;

        .ol-btn__inner {
          animation: fadeInHover 0.6s cubic-bezier(0.25, 0.8, 0.5, 1) forwards;
        }
      }

      &--not-hover {
        .ol-btn__inner {
          animation: 0.6s fadeOutHover ease-in-out;
        }
      }
    }
  }

  &__badge {
    position: absolute;
    top: 8px;
    right: 8px;
    padding: 2px 4px;
    background: #ffffff;
    border-radius: 2px;
    text-transform: uppercase;
    font-size: 11px;
    font-weight: 800;
    line-height: 12px;
    color: #121212eb;
    z-index: 4;
    box-shadow: 1px 1px 0px 0px #00000066;

    &--soon {
      background: var(--Alert-Purple-700, #865ed4);
      color: #ffffff;
    }

    &--expires {
      background: #eb509a;
      color: #ffffff;
    }
  }

  @media (max-width: 1024px) {
    gap: 4px;
    padding: 0 8px 8px;
    margin-top: 8px;

    .promo-item {
      &__img {
        max-width: 148px;
        margin-top: -8px;

        img {
          aspect-ratio: 148 / 136;
        }
      }

      &__description {
        margin-bottom: 12px;
      }

      &__body {
        min-height: 120px;
      }

      &__title {
        font-size: 16px;
        line-height: 20px;
        margin-bottom: 4px;
      }

      &__text {
        font-size: 12px;
        line-height: 16px;
      }

      &__button {
        font-size: 14px !important;
        height: 32px;
      }

      &__badge {
        font-size: 9px;
        line-height: 10px;
        padding: 1px 2px;
      }
    }
  }

  @media (max-width: 768px) {
    border-radius: 8px;

    .promo-item {
      &__img {
        max-width: 104px;

        img {
          aspect-ratio: 104 / 96;
        }
      }

      &__description {
        margin-bottom: 8px;
      }

      &__body {
        min-height: 112px;
      }

      &__title {
        font-size: 14px;
        line-height: 18px;
      }

      &__badge {
        font-size: 6px;
        line-height: 6px;
        padding: 2px 2px;
      }
    }
  }
}

@keyframes opacityChange {
  0% {
    background-color: rgba(255, 255, 255, 0.07);
  }

  50% {
    background-color: rgba(255, 255, 255, 0.01);
  }

  100% {
    background-color: rgba(255, 255, 255, 0.07);
  }
}

@keyframes fadeInHover {
  0% {
    background-color: rgba(255, 255, 255, 0.04);
    transform: translateY(-22%);
  }

  50% {
    transform: translateY(28%);
  }

  100% {
    background-color: rgba(255, 255, 255, 0.14);
    transform: translateY(20%);
  }
}

@keyframes fadeOutHover {
  0% {
    background-color: rgba(255, 255, 255, 0.14);
    transform: translateY(22%);
  }

  50% {
    transform: translateY(-27%);
  }

  100% {
    background-color: rgba(255, 255, 255, 0.015);
    transform: translateY(-22%);
  }
}
</style>
